// ==========================================
// 🔗 RE-EXPORTS DE SUB-MÓDULOS DE TIPOS
// ==========================================
// Tipos declarados em arquivos companheiros dentro de src/types/,
// re-exportados aqui para manter a API de import uniforme
// (consumidores fazem `import { X } from '@/types'` ou similar).

// ✅ B28.2pre (AI_50) — sub-interface de explante, rota C composition
// Declaração: src/types/achadosExplante.ts
export type { AchadosExplanteExtras } from "./achadosExplante";

// ==========================================
// 👥 USUÁRIOS E CATEGORIAS PROFISSIONAIS
// ==========================================

export type PerfilUsuario = "administrador" | "usuario" | "convidado";

// ========================================
// CATEGORIAS PROFISSIONAIS
// ========================================
// B89 (AI_102): categoria "outros" removida — não havia usuários nela e ela
// não é referenciada pelas regras de segurança (firestore.rules). Usuário sem
// categoria definida é tratado como médico (AuthContext).
export type CategoriaProfissional =
  | "medico"
  | "enfermeiro"
  | "psicologo"
  | "nutricionista"
  | "farmaceutico"
  | "administrativo";

// Tipos de registros profissionais (conselhos de classe)
export type TipoRegistroProfissional =
  | "CRM" // Conselho Regional de Medicina
  | "COREN" // Conselho Regional de Enfermagem
  | "CRP" // Conselho Regional de Psicologia
  | "CRN" // Conselho Regional de Nutrição
  | "CRF"; // Conselho Regional de Farmácia

// Interface para registro profissional
export interface RegistroProfissional {
  numero: string; // Número do registro (ex: "123456")
  uf: string; // UF do conselho (ex: "SP", "RJ")
  tipo: TipoRegistroProfissional; // Tipo do conselho
}

// ==========================================
// 🏥 DADOS DO SOLICITANTE (B211 — formulários auxiliares SUS)
// ==========================================
// Dados fixos da unidade/profissional reaproveitados em todos os formulários
// auxiliares do SUS (ficha GAL, APAC, LME...). Preenchidos uma vez no cadastro
// do usuário e reusados a cada geração.
export interface DadosSolicitante {
  cnsProfissional?: string; // CNS do profissional de saúde (GAL campo 7)
  cpfProfissional?: string; // CPF do profissional (carga viral HBV/HCV/HIV campo 7)
  unidadeSaude?: string; // Nome da unidade de saúde solicitante (GAL campo 2)
  cnes?: string; // CNES da unidade solicitante (GAL campo 3)
  municipioAtendimento?: string; // Município de atendimento (GAL campo 4)
  codigoIBGE?: string; // Código IBGE do município (GAL campo 5)
  ufAtendimento?: string; // UF da unidade solicitante (GAL campo 6)
}

// Mapeamento automático: cada categoria tem seu tipo de registro
export const CATEGORIA_REGISTRO_MAP: Record<
  Exclude<CategoriaProfissional, "administrativo">,
  TipoRegistroProfissional
> = {
  medico: "CRM",
  enfermeiro: "COREN",
  psicologo: "CRP",
  nutricionista: "CRN",
  farmaceutico: "CRF",
};

// Labels para exibição na interface
export const CATEGORIA_LABELS: Record<CategoriaProfissional, string> = {
  medico: "Médico",
  enfermeiro: "Enfermeiro",
  psicologo: "Psicólogo",
  nutricionista: "Nutricionista",
  farmaceutico: "Farmacêutico",
  administrativo: "Administrativo",
};

// Lista de UFs brasileiras
export const UFS_BRASIL = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
] as const;

export type UF = (typeof UFS_BRASIL)[number];

// Função helper: verifica se categoria precisa de registro profissional
export function categoriaRequerRegistro(
  categoria: CategoriaProfissional
): boolean {
  return categoria !== "administrativo";
}

// Função helper: pega o tipo de registro baseado na categoria
export function getTipoRegistro(
  categoria: CategoriaProfissional
): TipoRegistroProfissional | null {
  if (!categoriaRequerRegistro(categoria)) return null;
  return CATEGORIA_REGISTRO_MAP[
    categoria as keyof typeof CATEGORIA_REGISTRO_MAP
  ];
}

// ========================================
// INTERFACE USUARIO ATUALIZADA
// ========================================
export interface Usuario {
  id: string;
  nome: string;
  email: string;
  usuario?: string; // ← MANTIDO (compatibilidade)
  senha?: string; // ← MANTIDO (compatibilidade)
  tipo: "administrador" | "usuario" | "convidado";

  // ========================================
  // ⭐ NOVOS CAMPOS - CATEGORIA PROFISSIONAL
  // ========================================
  categoria: CategoriaProfissional; // ← NOVO: categoria profissional
  registroProfissional?: RegistroProfissional; // ← NOVO: registro do conselho de classe

  // ========================================
  // CAMPOS LEGADOS (manter compatibilidade)
  // ========================================
  crm?: string; // ← DEPRECATED: será migrado para registroProfissional
  especialidade?: string; // ← MANTIDO: especialidade médica

  // ========================================
  // 🏥 DADOS DO SOLICITANTE (B211 — formulários auxiliares SUS)
  // ========================================
  dadosSolicitante?: DadosSolicitante; // ← NOVO: unidade/CNS/CNES p/ GAL etc.

  // Campos de controle
  ativo: boolean;
  criadoEm?: string;
  atualizadoEm?: string;
  perfil?: PerfilUsuario; // ← MANTIDO (compatibilidade)

  // 🔒 B103 (conformidade CFM 2.454) — troca de senha obrigatória no 1º acesso.
  // Setado true ao criar o usuário (senha temporária); limpado ao trocar a senha.
  precisaTrocarSenha?: boolean;
}

// ==========================================
// 👤 PACIENTES
// ==========================================

export interface Paciente {
  // ============================================
  // IDENTIFICAÇÃO
  // ============================================
  id: string;

  // ============================================
  // 👤 DADOS PESSOAIS BÁSICOS
  // ============================================
  nome: string;
  cpf?: string;
  rg?: string;
  cns?: string; // Cartão Nacional de Saúde
  rgct?: string; // Número RGCT para inscritos
  hospub?: string; // Cartão SUS
  dataNascimento: string; // Mantém string por compatibilidade (converte depois)
  sexo: "M" | "F";

  // ============================================
  // 📱 CONTATOS
  // ============================================
  telefone1?: string;
  telefone2?: string;
  telefone3?: string;
  email?: string;

  // ============================================
  // 📍 ENDEREÇO (CAMPOS SEPARADOS)
  // ============================================
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;

  // ============================================
  // 🚨 CONTATO DE EMERGÊNCIA (CAMPOS SEPARADOS)
  // ============================================
  contatoEmergenciaNome?: string;
  contatoEmergenciaTelefone?: string;
  contatoEmergenciaRelacao?: string;

  // ============================================
  // 🏥 DADOS CLÍNICOS
  // ============================================
  peso?: number;
  altura?: number;
  tipoSanguineo?: "A+" | "A-" | "B+" | "B-" | "AB+" | "AB-" | "O+" | "O-";
  alergias?: string;

  // ============================================
  // 🏥 STATUS DO TRANSPLANTE
  // ============================================
  statusTransplante: StatusTransplante;

  // ============================================
  // 📊 DADOS DO TRANSPLANTE (quando aplicável)
  // ============================================
  dataInscricao?: string;
  situacaoEspecial?: boolean;
  dataAprovacaoSE?: string;
  tipoSE?: string;
  dataTransplante?: string;
  hospitalTransplante?: string;

  // ============================================
  // 📌 STATUS DO PACIENTE NO SERVIÇO
  // ============================================
  statusPaciente?:
    | "ativo"
    | "obito"
    | "perda_seguimento"
    | "retirado_lista"
    | "transferido"
    | "alta";
  dataStatusPaciente?: string;
  motivoStatus?: string;

  // ============================================
  // 📋 AUDITORIA & LGPD
  // ============================================
  cadastradoPor?: string;
  cadastradoPorNome?: string;
  editadoPor?: string;
  editadoPorNome?: string;
  dataCadastro: string;
  dataUltimaEdicao?: string;
  ativo?: boolean;

  // ============================================
  // 📜 HISTÓRICO (OPCIONAL - pode implementar depois)
  // ============================================
  historicoEdicoes?: HistoricoEdicao[];

  // ============================================
  // 🏥 DADOS DO TRANSPLANTE (Estrutura complexa - manter)
  // ============================================
  dadosTransplante?: {
    equipeCirurgica?: {
      cirurgiao?: string;
      auxiliar1?: string;
      auxiliar2?: string;
      auxiliar3?: string;
      fellow?: string;
      residente?: string;
      academico?: string;
      instrumentador?: string;
      anestesista1?: string;
      anestesista2?: string;
      backTable?: string;
      editadoEm?: string;
    };
    dadosComplementares?: {
      clampeDoador?: string;
      inicioCirurgia?: string;
      fimCirurgia?: string;
      perfusaoPortalSolucao?: string;
      perfusaoPortalVolume?: string;
      perfusaoPortalTempo?: string;
      perfusaoArterialSolucao?: string;
      perfusaoArterialVolume?: string;
      perfusaoArterialTempo?: string;
      concHemacias?: string;
      plasma?: string;
      plaquetas?: string;
      crioprecipitado?: string;
      complexoProtrombina?: string;
      concFibrinogenio?: string;
      cellSaver?: string;
      atbProfilatico?: string;
      editadoEm?: string;
    };
    tecnicaOperatoria?: {
      pesoEnxerto?: string;
      metodo?: string;
      clampamentoPorta?: string;
      figadoForaGelo?: string;
      revascularizacaoPortalHora?: string;
      revascularizacaoPortalLocal?: string;
      revascularizacaoArterialHora?: string;
      revascularizacaoArterialLocal?: string;
      drenagemVenosa?: string;
      drenagemVenosaDescricao?: string;
      drenagemCavaCava?: string;
      reconstrucaoBiliar?: string;
      editadoEm?: string;
    };
    posOperatorio?: {
      dataExtubacao?: string;
      horaExtubacao?: string;
      diasUTI?: string;
      dataAlta?: string;
      observacoes?: string;
      editadoEm?: string;
    };
  };
  // ============================================
  // 🏥 MÚLTIPLOS TRANSPLANTES
  // ============================================
  transplantes?: {
    ordem: number;
    dataTransplante?: string;
    motivoRetx?: string;
    headerEditadoEm?: string;
    equipeCirurgica?: {
      cirurgiao?: string;
      auxiliar1?: string;
      auxiliar2?: string;
      auxiliar3?: string;
      fellow?: string;
      residente?: string;
      academico?: string;
      instrumentador?: string;
      anestesista1?: string;
      anestesista2?: string;
      backTable?: string;
      editadoEm?: string;
    };
    dadosComplementares?: {
      clampeDoador?: string;
      inicioCirurgia?: string;
      fimCirurgia?: string;
      perfusaoPortalSolucao?: string;
      perfusaoPortalVolume?: string;
      perfusaoPortalTempo?: string;
      perfusaoArterialSolucao?: string;
      perfusaoArterialVolume?: string;
      perfusaoArterialTempo?: string;
      concHemacias?: string;
      plasma?: string;
      plaquetas?: string;
      crioprecipitado?: string;
      complexoProtrombina?: string;
      concFibrinogenio?: string;
      cellSaver?: string;
      atbProfilatico?: string;
      editadoEm?: string;
    };
    tecnicaOperatoria?: {
      pesoEnxerto?: string;
      metodo?: string;
      clampamentoPorta?: string;
      figadoForaGelo?: string;
      revascularizacaoPortalHora?: string;
      revascularizacaoPortalLocal?: string;
      revascularizacaoArterialHora?: string;
      revascularizacaoArterialLocal?: string;
      drenagemVenosa?: string;
      drenagemVenosaDescricao?: string;
      drenagemCavaCava?: string;
      reconstrucaoBiliar?: string;
      editadoEm?: string;
    };
    posOperatorio?: {
      dataExtubacao?: string;
      horaExtubacao?: string;
      diasUTI?: string;
      dataAlta?: string;
      observacoes?: string;
      editadoEm?: string;
    };
  }[];
  // ============================================
  // 🔙 COMPATIBILIDADE (DEPRECATED - remover depois)
  // ============================================
  tipoAtendimento?: string;
  interfaceAtual?: TipoInterface;
  grupoSanguineo?: string; // Alias para tipoSanguineo
  telefone?: string; // Alias para telefone1
  endereco?: any; // Mantém por compatibilidade
  contatoEmergencia?: any; // Mantém por compatibilidade
  inscricaoTx?: any; // Mantém por compatibilidade

  // ✅ ADICIONAR ESTES CAMPOS:
  nomeMae?: string;
  estadoCivil?: string;
  profissao?: string;
  medicacoesEmUso?: string;
  comorbidades?: string;
  centroTransplantador?: string;
  equipeMedica?: string;
  tipoDoador?: string;
  tempoIsquemia?: string;
  numeroRGCT?: string;
  dataInclusaoLista?: string;
  // ============================================
  // 🏥 AVALIAÇÃO CLÍNICA
  // ============================================
  // B351 (AI_152) — avaliação do DOADOR vivo de fígado. Objeto SEPARADO do
  // `avaliacaoClinica` de propósito: muitos doadores foram cadastrados antes como
  // "figado"/"ambulatorial" e convertidos depois, então os dois conjuntos precisam
  // conviver sem colisão (a conversão não apaga nada e é reversível).
  // Campos livres: a aba evolui com a prática e o consenso ILTS-iLDLT.
  avaliacaoDoador?: Record<string, any>;

  avaliacaoClinica?: {
    causaCirrose?: string;
    // Abstinência alcoólica (AI_141) — data da última ingesta; o tempo de
    // abstinência é calculado (ver abstinenciaAlcoolica.ts). Só relevante em
    // etiologia alcoólica.
    ultimaIngestaAlcool?: string;
    ascite?: boolean;
    paracentese?: boolean;
    paracenteseVolume?: string;
    paracenteseNumero?: string; // nº de paracenteses já realizadas (AI_141)
    // Hidrotórax hepático (AI_141) — condição própria, avaliada após ascite.
    hidrotorax?: boolean;
    hidrotoraxSessoes?: string; // nº de toracocenteses
    hidrotoraxVolume?: string; // maior volume retirado (litros)
    varizesEsofago?: boolean;
    varizesGastrica?: boolean;
    hdaPrevia?: boolean;
    hdaEpisodios?: string;
    ligaduraElastica?: boolean;
    ligaduraSessoes?: string;
    encefalopatia?: boolean;
    encefalopatiaEpisodios?: string;
    encefalopatiaGrau?: string;
    pbe?: boolean;
    // Empiema pleural bacteriano espontâneo (AI_141) — infecção espontânea do
    // hidrotórax hepático (análogo à PBE no espaço pleural).
    empiemaBacterianoEspontaneo?: boolean;
    // Colangite bacteriana de repetição (AI_141) — recorrente em CEP/CBS e em
    // estenose/anastomose biliar.
    colangiteRepeticao?: boolean;
    colangiteEpisodios?: string; // nº de episódios
    // Colangiografia/drenagem trans-parieto-hepática percutânea (CTPH) (AI_141).
    colangioTransparietoHepatica?: boolean;
    colangioTransparietoHepaticaData?: string;
    shr?: boolean;
    shrTipo?: string;
    // Terapia de substituição renal / diálise (AI_141) — SLK e MELD.
    dialise?: boolean;
    dialiseModalidade?: string; // HD, DP, CVVH...
    dialiseDesde?: string; // data de início
    // Hipertensão portopulmonar (AI_141) — se SIM, grau leve/moderada/grave.
    hipertensaoPortopulmonar?: boolean;
    hipertensaoPortopulmonarGrau?: string;
    shdHepatopulmonar?: boolean;
    // Prurido intratável (AI_141).
    pruridoIntratavel?: boolean;
    // Insuficiência hepática AGUDA/fulminante (sem doença crônica prévia) —
    // distinta do ACLF (agudização sobre cirrose), abaixo (AI_141).
    insuficienciaHepaticaAguda?: boolean;
    aclf?: boolean;
    aclfGrau?: string; // "1" | "2" | "3" (CLIF)
    // TIPS — shunt portossistêmico transjugular (AI_141).
    tips?: boolean;
    tipsData?: string;
    sarcopenia?: boolean;
    lfiScore?: string;
    lfiClassificacao?: string;
    // 🎗️ Hepatocarcinoma (CHC) — diagnóstico estruturado (B248/AI_111). Fonte da
    // verdade para a pesquisa (NÃO depende de situação especial: HCC avançado/não
    // listado nunca teria SE). hccMilao: "Dentro" | "Fora"; hccBCLC: "0"|"A".."D".
    hepatocarcinoma?: boolean;
    hccDataDiagnostico?: string;
    hccMilao?: string;
    hccBCLC?: string;
    // Terapia prévia do CHC (AI_141) — dois eixos:
    //  • Modalidade (`hccTerapiaLocorregionalTipos`, múltiplas): TACE, ablação,
    //    radioembolização, terapia sistêmica (sorafenibe/imunoterapia).
    //  • Intenção (`hccTerapiaIntencao`): Ponte (bridging) | Downstaging |
    //    Definitivo. (Downstaging/ponte NÃO são modalidades — são a intenção.)
    hccTerapiaLocorregional?: boolean;
    hccTerapiaLocorregionalTipos?: string[];
    hccTerapiaIntencao?: string;
  };
  // ============================================
  // 📊 ESCORES (calculados automaticamente)
  // ============================================
  scores?: {
    meld?: number;
    meldNa?: number;
    childPugh?: string;
  };
  // 🧬 B312-FU/AI_141 — última análise de IA dos marcadores, persistida para
  // reabrir depois (botão "Ver última análise"). Só o texto + proveniência leve.
  ultimaAnaliseMarcadores?: {
    texto: string;
    geradaEm: string; // ISO
    geradaPor?: string;
    geradaPorNome?: string;
  };
  // 🧫 B313/AI_141 — última análise de IA de sorologias (mesmo esquema).
  ultimaAnaliseSorologias?: {
    texto: string;
    geradaEm: string; // ISO
    geradaPor?: string;
    geradaPorNome?: string;
  };
  // 🤖 B314/AI_141 — última análise de IA de consulta/evolução (guarda também o
  // id da consulta, para o botão "Ver análise anterior" aparecer no card certo).
  ultimaAnaliseEvolucao?: {
    texto: string;
    geradaEm: string; // ISO
    evolucaoId: string;
    geradaPor?: string;
    geradaPorNome?: string;
  };
  // 🧪 B323-FU/AI_145 — última análise de IA dos EXAMES LABORATORIAIS. Diferente
  // das 3 acima (texto markdown): a análise lab é o objeto AnaliseIA ESTRUTURADO
  // (selos/achados/alertas/sugestões), que o modal renderiza. `analise` é tipado
  // como `unknown` para NÃO criar ciclo de import com ia-analise/types (que já
  // importa de src/types); o consumidor (ExamesLaboratoriais) faz o cast p/ AnaliseIA.
  ultimaAnaliseLab?: {
    analise: unknown;
    geradaEm: string; // ISO
    geradaPor?: string;
    geradaPorNome?: string;
  };
}

// ✅ NOVO: Interface para histórico de edições
export interface HistoricoEdicao {
  data: string;
  usuarioId: string;
  usuarioNome: string;
  acao: "cadastro" | "edicao" | "visualizacao";
  camposAlterados?: string[]; // Lista de campos que foram alterados
}

export enum TipoTransplante {
  FIGADO = "figado",
  RIM = "rim",
  PANCREAS = "pancreas",
  RIM_PANCREAS = "rim_pancreas",
}

export enum StatusTransplante {
  PRE_TRANSPLANTE = "pre",
  LISTA_ESPERA = "lista",
  POS_TRANSPLANTE = "pos",
}

export enum TipoInterface {
  AMBULATORIAL = "ambulatorial",
  PRE_TX_FIGADO = "pre_tx_figado",
  PRE_TX_RIM = "pre_tx_rim",
  PRE_TX_PANCREAS = "pre_tx_pancreas",
  POS_TX_FIGADO = "pos_tx_figado",
  POS_TX_RIM = "pos_tx_rim",
  POS_TX_PANCREAS = "pos_tx_pancreas",
}

// ═══════════════════════════════════════════════════════
// EVOLUÇÃO CLÍNICA
// ═══════════════════════════════════════════════════════

// B90 (AI_102): tipo de consulta na evolução
export type TipoConsulta =
  | "primeira-consulta"
  | "retorno"
  | "pre-transplante"
  | "pos-transplante"
  | "intercorrencia";

export const TIPO_CONSULTA_LABELS: Record<TipoConsulta, string> = {
  "primeira-consulta": "Primeira consulta",
  retorno: "Retorno",
  "pre-transplante": "Pré-transplante",
  "pos-transplante": "Pós-transplante",
  intercorrencia: "Intercorrência",
};

// Tipos "focados" que usam um campo ÚNICO de evolução (texto livre) em vez do
// formulário estruturado de anamnese. Os demais (primeira consulta, pré-Tx)
// usam o formulário estruturado completo.
export const TIPOS_CONSULTA_CAMPO_UNICO: TipoConsulta[] = [
  "retorno",
  "pos-transplante",
  "intercorrencia",
];

export interface Evolucao {
  id: string;
  pacienteId: string;
  data: string; // ISO timestamp

  // Dados da Evolução
  queixaPrincipal?: string;
  historiaDoencaAtual?: string;
  revisaoSistemas?: string;
  exameFisico?: string;
  hipotesesDiagnosticas?: string;
  conduta?: string;
  observacoes?: string;
  // B90 (AI_102): classificação da consulta + evolução em campo único
  tipoConsulta?: TipoConsulta;
  evolucaoLivre?: string; // texto único (retorno/pós-Tx/intercorrência)
  // Sinais vitais e antropometria
  peso?: number;
  altura?: number;
  fc?: number;
  pa?: string;

  // Auditoria
  registradoPor: string;
  registradoPorNome: string;
  editadoPor?: string;
  editadoPorNome?: string;
  dataEdicao?: string;
  // B323 (AI_145): carimbo de criação (serverTimestamp → ISO no onSnapshot).
  // Base da trava de 24h (imutável na regra); espelha marcador/sorologia.
  criadoEm?: string;

  // B323 Bloco 3 (AI_146): cancelamento não-destrutivo. Registro errado antigo
  // NÃO é apagado nem sobrescrito — cancela-se com motivo (fica no banco,
  // marcado, some das telas normais e da IA). Espelha o modelo das prescrições.
  cancelado?: boolean;
  canceladoPor?: string;
  canceladoPorNome?: string;
  canceladoEm?: string;
  motivoCancelamento?: string;

  // IA (opcional)
  analisePorIA?: {
    resumo?: string;
    sugestoes?: string[];
    alertas?: string[];
  };
}

// ==========================================
// 📊 MARCADORES E AUTOANTICORPOS
// ==========================================

export interface MarcadorAutoanticorpo {
  id: string;
  pacienteId: string;
  dataColeta: string; // ISO string

  // Marcadores Tumorais
  afp?: string;
  ca199?: string;
  cea?: string;
  ca125?: string;
  psa?: string;

  // Metabolismo do ferro (B241 fase 2 — AI_109; exames de 1-2x, ficam em marcadores)
  ferro?: string;
  ferritina?: string;
  saturacaoTransferrina?: string;

  // Marcadores Metabólicos / Etiológicos (B195)
  cobreSerico?: string;
  cobreUrinario?: string;
  ceruloplasmina?: string;
  alfa1Antitripsina?: string;

  // Função renal / urina 24h (B279 — AI_120; exames ocasionais, ficam em marcadores)
  clearanceCreatinina?: string; // depuração de creatinina total (mL/min)
  clearanceCreatininaCorrigido?: string; // corrigido p/ 1,73m² (mL/min/1,73m²)
  proteinuria24h?: string; // proteinúria de 24h (mg/24h)
  sodioUrinarioAmostra?: string; // sódio urinário, amostra isolada (mEq/L) (AI_129)
  sodioUrinario24h?: string; // sódio urinário, urina de 24h (mEq/24h) (AI_129)

  // Imunoglobulinas / Autoimune-IgG4 (B195)
  iggSerico?: string;
  igg4?: string;

  // Eletroforese de proteínas — frações em g/dL (B195)
  efpAlbumina?: string;
  efpAlfa1?: string;
  efpAlfa2?: string;
  efpBeta?: string;
  efpGama?: string;

  // Autoanticorpos
  fan?: string;
  antiLKM?: string;
  antiMusculoLiso?: string;
  antiMitocondria?: string;
  anca?: string;
  antiDNA?: string;
  antiTransglutaminase?: string;

  // Auditoria
  registradoPor: string;
  registradoPorNome: string;
  editadoPor?: string;
  editadoPorNome?: string;
  dataEdicao?: string;
  criadoEm?: string;
}
// ==========================================
// 🦠 SOROLOGIAS VIRAIS
// ==========================================

export interface SorologiaViral {
  id: string;
  pacienteId: string;
  dataColeta: string;

  // Hepatite B
  hbsag?: string;
  antiHBs?: string;
  antiHBcIgG?: string;
  antiHBcIgM?: string;
  hbeag?: string;
  antiHBe?: string;
  genotipagemVHB?: string;
  pcrVHB?: string;

  // Hepatite C
  antiHCV?: string;
  hcvRNA?: string;

  // Hepatite A
  antiHAVIgG?: string;
  antiHAVIgM?: string;

  // Outras Hepatites
  antiHDV?: string;
  pcrVHD?: string;
  antiHEV?: string;

  // CMV
  cmvIgG?: string;
  cmvIgM?: string;
  cmvPCR?: string;

  // EBV
  ebvIgG?: string;
  ebvIgM?: string;
  ebvPCR?: string;

  // Rubéola
  rubeolaIgG?: string;
  rubeolaIgM?: string;

  // Outros
  antiHIV?: string;
  vdrl?: string;
  ftaAbs?: string;
  toxoIgG?: string;
  toxoIgM?: string;
  chagas?: string; // legado: "Chagas (geral)" — mantido p/ não perder dados antigos
  chagasIgG?: string; // B241 fase 2 (AI_109)
  chagasIgM?: string; // B241 fase 2 (AI_109)
  htlv?: string;

  // Rastreio pré-Tx — parasitas / tuberculose (AI_118): risco de reativação
  // sob imunossupressão (estrongiloidíase = hiperinfecção; TB latente).
  estrongiloidesIgG?: string; // anti-Strongyloides IgG (qualitativo)
  igraTB?: string; // IGRA / Quantiferon-TB (qualitativo)
  ppd?: string; // PPD / tuberculina (texto, em mm)

  // Auditoria
  registradoPor: string;
  registradoPorNome: string;
  editadoPor?: string;
  editadoPorNome?: string;
  dataEdicao?: string;
  criadoEm?: string;
}

// ==========================================
// 📷 EXAMES DE IMAGEM E PROCEDIMENTOS
// ==========================================

/**
 * 🔬 ADEQUAÇÃO AMOSTRAL — AI_40 Patch 2 (Bloco 2 Camada 5)
 * Retornado pela action `avaliar-adequacao-amostral` (api/_lib/adequacaoAmostral.js).
 * Classificação determinística AASLD 2009 + EASL 2015:
 *   adequada      = ≥15mm E ≥11 EP
 *   marginal      = (≥10mm E ≥6 EP) sem atender adequada
 *   inadequada    = <10mm OU <6 EP
 *   nao_avaliavel = laudo omite pelo menos um dos critérios
 */
export interface AdequacaoAmostral {
  classificacao: "adequada" | "marginal" | "inadequada" | "nao_avaliavel";
  comprimentoMm: number | null;
  espacosPorta: number | null;
  fonteExtracao: "tripla_dimensional" | "descricao_simples" | "nao_extraido";
  avaliadoEm: string; // ISO timestamp

  // Campos populados pela Alt 5 (integração detectorContraditorios) — opcionais
  contextoContraditorio?: {
    temContraditorio: boolean;
    gatilhosAtivos: string[]; // ex: ['imagem_morfologica', 'biopsia_anterior']
    severidade?: "baixa" | "media" | "alta";
  };
  classificacaoFinal?:
    | "adequada"
    | "marginal"
    | "inadequada"
    | "nao_avaliavel"
    | "nao_confiavel_isolada";
}

export interface ExameImagem {
  id: string;
  pacienteId: string;
  dataExame: string;
  tipoExame:
    | "Ultrassonografia"
    | "Tomografia"
    | "Ressonância Magnética"
    | "Radiografia"
    | "Elastografia"
    | "Cintilografia"
    | "Ecocardiograma"
    | "Densitometria Óssea"
    | "CPRE"
    | "Ecoendoscopia"
    | "PET Scan"
    | "Endoscopia"
    | "Colonoscopia"
    | "Colangiorressonância"
    | "Quimioembolização (TACE)"
    | "Biópsia"
    | "Outros";
  local?: string;
  medicoSolicitante?: string;
  indicacaoClinica?: string;
  laudo?: string;
  arquivo?: {
    nome: string;
    tipo: string;
    url: string; // Base64 ou URL
  };

  // ✅ Achados da IA
  achados?: {
    tipo: string;
    data: string;
    lesoesHepaticas: string;
    statusMilao: "DENTRO" | "FORA" | "NÃO APLICÁVEL" | "";
    trombosePortal: string;
    tipoTrombose: "BLAND" | "TUMORAL" | "NÃO APLICÁVEL" | "";
    shunts: string;
    ascite: "SIM" | "NÃO" | "";
    esplenomegalia: string;
    outrosAchados: string;
    conclusao: string;
    // ✅ Bloco 1 AI_34 — transcrição literal do laudista (Haiku vision)
    // O campo `conclusao` acima é interpretação INTEGRADA da IA.
    // Os campos abaixo são a transcrição VERBATIM do que o laudista (humano) escreveu.
    // Quando ambos divergem, o literal abaixo prevalece como fonte primária.
    conclusaoLaudistaLiteral?: string;
    tipoLaudista?:
      | "patologista"
      | "radiologista"
      | "endoscopista"
      | "ecografista"
      | "cardiologista"
      | "outro";
    conclusaoLiteralGeradaEm?: string;
    conclusaoLiteralStatus?:
      | "sucesso"
      | "sem_secao_conclusao"
      | "erro_extracao";

    // ✅ AI_40 Patch 2 — adequação amostral (só para biópsias)
    // Populado pela action `avaliar-adequacao-amostral` (determinística, <5ms)
    // Para exames não-biópsia: permanece undefined
    adequacaoAmostral?: AdequacaoAmostral;

    // ✅ B301 (AI_137) — campos ESTRUTURADOS de ENDOSCOPIA (EDA/colonoscopia),
    // extraídos por extrairCamposEndoscopia. Consumidos pelo insight de CSPH
    // (ProntuarioAlertasIA) para detectar varizes de forma precisa.
    varizesEsofagicas?: string;
    varizesGastricas?: string;
    gastropatiaHipertensiva?: string;
    sinaisRisco?: string;
    gave?: string;
    classificacaoRisco?: string;

    // ✅ B301 cont. (AI_138) — campos ESTRUTURADOS de ELASTOGRAFIA (FibroScan),
    // extraídos por extrairCamposElastografia. Lidos pelo insight de CSPH
    // (ProntuarioAlertasIA) para a rigidez precisa (antes só regex do laudo).
    rigidezKpa?: string;
    fibroseMetavir?: string;
    capEsteatose?: string;
  };
  resumoAchados?: string;
  // ✅ Texto COMPLETO da análise da IA (fonte mais rica; usado quando a IA não
  // emite achados estruturados — ver fallback em ListaExamesImagem).
  analiseCompleta?: string;

  // Auditoria
  registradoPor: string;
  registradoPorNome: string;
  editadoPor?: string;
  editadoPorNome?: string;
  dataEdicao?: string;
  // B323 (AI_145): carimbo de criação (serverTimestamp → ISO). Base da trava
  // de 24h (imutável na regra); espelha marcador/sorologia/evolução.
  criadoEm?: string;
}
